import React, { useEffect, useState } from 'react';
import { toast } from "sonner";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Check, Circle, FileText, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils.ts";
import { PHQ9_DATA, GAD7_DATA } from "@/lib/questions.ts";
import type { routesTypes } from "@/lib/routes.ts";
import routes from "@/lib/routes.ts";

import { Button } from "@/components/ui/button.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card.tsx';
import AuthService from "@/lib/auth";

const defaultRoutes: routesTypes = {
  id: "",
  type: "",
  title: "",
  next: "",
  continue: ""
}

const PublicQuestionnaire: React.FC = () => {
  const navigate = useNavigate();
  const { type, patientId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [route, setRoute] = useState<routesTypes>(defaultRoutes);
  const [currentStep, setCurrentStep] = useState<"questionnaire" | "review" | "complete">("questionnaire");
  const [questions, setQuestions] = useState<any[]>([]);
  const [accessCode, setAccessCode] = useState<string | null>(null);
  const [verifiedPatientId, setVerifiedPatientId] = useState<string | null>(null);
  const [patientName, setPatientName] = useState<string>('');
  

  // Verify access when component mounts (RDS via API)
  useEffect(() => {
    const verifyAccess = async () => {
      const searchParams = new URLSearchParams(location.search);
      const code = searchParams.get('access_code');

      if (!code) {
        toast.error('Invalid or missing access information');
        navigate('/');
        return;
      }

      try {
        const result = await AuthService.verifyQuestionnaireAccess(code);
        if (result?.error) {
          toast.error(typeof result.error === 'string' ? result.error : 'Invalid or expired access code');
          navigate('/');
          return;
        }
        const { patientId: vId, patientName: verifiedName } = result as { patientId: string; patientName: string; accessCode: string };
        if (patientId && vId !== patientId) {
          toast.error('Access code does not match patient');
          navigate('/');
          return;
        }
        setVerifiedPatientId(vId);
        setPatientName(verifiedName || 'Patient');
        setAccessCode(code);
        setIsVerifying(false);
      } catch (error) {
        console.error('Error verifying access:', error);
        toast.error('An error occurred while verifying access');
        navigate('/');
      }
    };

    verifyAccess();
  }, [location, navigate, patientId]);

  // Load questionnaire data
  useEffect(() => {
    if (type !== undefined && type?.length > 0) {
      const _route = routes.find(item => item.id === type);
      if (_route !== undefined && _route !== null) {
        setRoute(_route);
      }
      if (type === "phq-9") {
        setQuestions(PHQ9_DATA);
      }
      if (type === "gad-7") {
        setQuestions(GAD7_DATA);
      }
    }
  }, [type]);

  const canContinue = () => {
    return questions.every((q) => q.selected !== null && q.selected !== undefined);
  };

  const handleResponse = (questionId: number, value: number) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId ? { ...q, selected: value } : q
      )
    );
  };

  const handleFormSubmit = async () => {
    if (!accessCode || !verifiedPatientId) {
      toast.error('Access verification failed');
      return;
    }

    setLoading(true);
    try {
      const totalScore = questions.reduce((sum, q) => sum + (typeof q.selected === "number" ? q.selected : 0), 0);
      const submitData = {
        patient_id: verifiedPatientId,
        questionnaire_type: route.type,
        answers: questions,
        total_score: totalScore,
        self_reported: true,
        notes: `Questionnaire completed via SMS link (${accessCode})`,
        status: 'pending for review',
        token: accessCode
      };
      const result = await AuthService.submitQuestionnaireResponse(submitData);
      if (result?.error) {
        toast.error((result.error as any)?.message || 'Error saving responses');
      } else {
        await AuthService.updateSMSLog(accessCode, { sent_times: 99 });
        setCurrentStep("complete");
        toast.success('Questionnaire submitted successfully!');
      }
    } catch (e: any) {
      toast.error(`Error saving ${route.title} responses. Please try again.`);
      console.error('Submit error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    // Redirect to a thank you page or close the window
    window.location.href = 'about:blank'; // Or redirect to your main website
  };

  const renderQuestionnaire = () => {
    // Calculate progress based on answered questions
    const answeredQuestions = questions.filter(q => q.selected !== null && q.selected !== undefined).length;
    const progressPercentage = questions.length > 0 ? (answeredQuestions / questions.length) * 100 : 0;

    return (
      <Card className="w-full bg-white shadow-md rounded-2xl overflow-hidden border border-gray-200 py-0">
        <CardHeader className="bg-gradient-to-r from-green-400 to-lime-200 p-6 rounded-t-2xl">
          <div className="flex items-center mb-4">
            <FileText className="mr-3 text-white" size={28} />
            <CardTitle className="text-3xl font-bold text-white">
              {route.title} Questionnaire
            </CardTitle>
          </div>
          <div className="w-full bg-white/30 rounded-full h-2">
            <div
              style={{
                width: `${progressPercentage}%`,
              }}
              className="h-2 rounded-full bg-gradient-to-r from-green-400 via-yellow-300 to-green-100 transition-all duration-300"
            ></div>
          </div>
          <p className="text-sm text-white mt-2">
            Hello {patientName} - Over the last 2 weeks, how often have you been bothered by any of the following problems?
          </p>
        </CardHeader>

        <CardContent className="space-y-6 p-6">
          {questions.map((q, index) => {
            return (
              <div key={index} className="bg-gradient-to-br from-green-50 via-yellow-50 to-green-100 p-5 rounded-xl border border-green-200 shadow-sm">
                <p className="font-semibold text-gray-700 mb-4 text-lg">
                  {index + 1}. {q.question}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                  {q.options.map((option: any) => {
                    const isSelected = q.selected === option.value;
                    return (
                      <div
                        key={option.value}
                        onClick={() => handleResponse(q.id, option.value)}
                        className={`flex items-center justify-start gap-2 px-4 py-3 rounded-lg cursor-pointer border transition-all duration-200 text-center text-sm font-medium ${
                          isSelected
                            ? "bg-green-100 border-green-600 text-green-800 shadow-inner ring-2 ring-green-500"
                            : "bg-white border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        {isSelected ? (
                          <Check className="text-green-600" size={18} />
                        ) : (
                          <Circle className="text-gray-400" size={18} />
                        )}
                        <span>{option.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </CardContent>

        <div className="flex flex-col md:flex-row md:justify-end p-6 pt-0 gap-4">
          <Button
            onClick={handleFormSubmit}
            disabled={loading || !canContinue()}
            className="w-full h-15 md:w-auto bg-green-600 hover:bg-green-700 text-white"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                Submit <Check className="ml-2" size={16} />
              </>
            )}
          </Button>
        </div>
      </Card>
    );
  };

  const renderReview = () => {
    const totalScore = questions.reduce((sum, q) => {
      return sum + (typeof q.selected === "number" ? q.selected : 0);
    }, 0);

    return (
      <Card className="max-w-4xl mx-auto bg-white shadow-md rounded-2xl overflow-hidden border border-gray-200 py-0">
        <CardHeader className="bg-gradient-to-r from-green-400 to-lime-200 p-6 rounded-t-2xl">
          <CardTitle className="text-3xl font-bold text-white mb-2">Review Your Answers</CardTitle>
          <div className="w-full bg-white/30 rounded-full h-2">
            <div className="bg-gradient-to-r from-green-400 via-yellow-300 to-green-100 h-2 rounded-full w-full"></div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6 p-6">
          {questions.map((q, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-green-50 via-yellow-50 to-green-100 p-5 rounded-xl border border-green-200 shadow-sm"
            >
              <p className="font-semibold text-gray-700 mb-2 text-lg">
                {index + 1}. {q.question}
              </p>
              <p className="text-green-700 font-medium text-base">
                {q.options.find((opt: any) => opt.value === q.selected)?.label || "Not answered"}
              </p>
            </div>
          ))}

          <Separator />

          <div className="bg-blue-50 p-5 rounded-xl shadow-sm border border-blue-200">
            <p className="font-semibold text-blue-800 text-lg">
              Total Score: {totalScore}/27
            </p>
          </div>
        </CardContent>

        <div className="flex flex-col md:flex-row justify-between gap-4 p-6 pt-0">
          <Button
            onClick={() => setCurrentStep("questionnaire")}
            variant="outline"
            className="w-full md:w-auto bg-white border-gray-300 hover:bg-gray-100 text-gray-700"
          >
            <ArrowLeft className="mr-2" size={16} />
            Edit Answers
          </Button>

          <Button
            onClick={handleFormSubmit}
            disabled={loading}
            className="w-full md:w-auto bg-green-600 hover:bg-green-700 text-white"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                Submit Final Answers <Check className="ml-2" size={16} />
              </>
            )}
          </Button>
        </div>
      </Card>
    );
  };

  const renderComplete = () => (
    <Card className="max-w-2xl mx-auto text-center bg-white shadow-md rounded-2xl border border-gray-200 overflow-hidden py-0">
      <CardHeader className="bg-gradient-to-r from-green-400 to-lime-200 p-6 rounded-t-2xl">
        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
          <Check className="text-green-600" size={32} />
        </div>
        <CardTitle className="text-2xl font-bold text-white mb-2">Questionnaire Completed Successfully</CardTitle>
        <p className="text-white/90 text-sm">
          Thank you {patientName} for completing the {route.title} questionnaire.
        </p>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        <div className="bg-green-50 p-4 rounded-xl border border-green-200 shadow-sm text-left">
          <p className="text-sm text-green-800 mb-2">
            <strong>Questionnaire Completed:</strong> {route.title}
          </p>
          <p className="text-sm text-green-800">
            Your responses have been securely saved and will be reviewed by your healthcare provider.
          </p>
        </div>

        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 shadow-sm text-left">
          <p className="text-sm text-blue-800">
            <strong>Next Steps:</strong> Your healthcare provider will review your responses and may contact you to discuss the results or schedule a follow-up appointment.
          </p>
        </div>

        <Button
          onClick={handleComplete}
          className={cn(
            "w-full mt-4 h-15 text-lg font-bold text-white",
            "bg-gradient-to-r from-green-400 to-green-500",
            "rounded-full shadow-lg shadow-green-300/40",
            "hover:scale-[1.02] transition-transform duration-150 ease-in-out",
            "flex items-center justify-center"
          )}
        >
          Complete
        </Button>
      </CardContent>
    </Card>
  );

  // Show loading while verifying access
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-green-500 mx-auto mb-4" />
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      {currentStep === "questionnaire" && renderQuestionnaire()}
      {currentStep === "review" && renderReview()}
      {currentStep === "complete" && renderComplete()}
    </div>
  );
};

export default PublicQuestionnaire;